import {
    DeviceType,
    Locale,
    MarketplaceId,
    OperatingSystem,
  } from "@amzn/books-design-system-web/build/dist/src/contexts/RenderContext";
import { CONTENT_TYPE } from "src/utils/asinUtils";
import { getCookieValue, KINDLE_STORE_COOKIE_NAME } from "src/utils/cookieUtils";
import { getCurrentBaseUrl, isURLAbsolute, setQueryParams } from "src/utils/urlUtils";
import { HttpRequestArgs } from "./ajaxUtils";
import debug from "./debugUtils";

const getiOSAPI = () => window.webkit?.messageHandlers;

const getConnectionStateAsString = (state?: number) => {
    if (state === 0) { return "None"; }
    if (state === 1) { return "WAN"; }
    if (state === 2) { return "WiFi"; }
    return "Unknown";
}

export type KindleStoreParametersI = {
    w?: string;
    h?: string;
    dpi?: string;
    deviceType?: string;
    osv?: string;
    appv?: string;
    browserMode?: string;
    storeType?: string;
    eid?: string;
    locale?: string;
    theme?: string;
    ref?: string;
};

export interface DeviceDetailsI {
    surfaceType: SURFACE_TYPE;
    isIOS: boolean;
    isTablet: boolean;
    isApp: boolean;
    isDesktop: boolean;
}

export function getDeviceDetails(): DeviceDetailsI {
    const surfaceType = getSurfaceType();
    return {
        surfaceType,
        isIOS: isIOSDevice(surfaceType),
        isTablet: isDeviceTablet(surfaceType),
        isApp: isApp(),
        isDesktop: surfaceType === SURFACE_TYPE.desktop,
    };
}

export function isApp(): boolean {
    // TODO: get from kstore instead?
    return true;
    // return window.ReadingInsights.deviceAttributes.includes("app");
}

export enum SURFACE_TYPE {
    desktop = "desktop",
    iOSMobile = "ios_mob",
    iOSTablet = "ios_tab",
}

export enum BookState {
    /** Book is in an unknown or error state */
    AZPluginBookStateInLibraryUnknown = 0,
    /** Book has been marked for deletion and will be removed from the library */
    AZPluginBookStateInLibraryPendingDeletion = 1,
    /** Book has been deleted from device and removed from Cloud. For sideloaded or non-owned pdocs */
    AZPluginBookStateInLibraryDeleted = 2,
    /** Book has been deleted from device and moved to Cloud. For owned books and pdocs */
    AZPluginBookStateInLibraryInCloud = 3,
    /** Book has been downloaded or sideloaded to device */
    AZPluginBookStateInLibraryOnDevice = 4,
    /** Book is being downloaded */
    AZPluginBookStateInLibraryDownloading = 5,
    /** An attempt to download the book was made and the download failed. */
    AZPluginBookStateInLibraryDownloadFailed = 6,
    /** Book download progress is blocked */
    AZPluginBookStateInLibraryDownloadBlocked = 7,
}

export function getSurfaceType(): SURFACE_TYPE {
    const ua = navigator.userAgent;
    const iPad13 = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1; // workaround to detect iOS 13 on iPad
    if (ua.includes("iPad") || iPad13) {
        return SURFACE_TYPE.iOSTablet;
    } else if (ua.includes("iPhone") || ua.includes("iPod")) {
        return SURFACE_TYPE.iOSMobile;
    } else {
        return SURFACE_TYPE.desktop;
    }
}

export function isDeviceTablet(surfaceType = getSurfaceType()): boolean {
    return surfaceType === SURFACE_TYPE.iOSTablet;
}

export function isIOSDevice(surfaceType = getSurfaceType()): boolean {
    return [SURFACE_TYPE.iOSMobile, SURFACE_TYPE.iOSTablet].includes(surfaceType);
}

export function isSharingSupported(): boolean {
    return typeof navigator.share === "function";
}

const getAbsoluteUrl = (url: string) => {
    return isURLAbsolute(url) ? new URL(url) : new URL(url, getCurrentBaseUrl());
}

/**
 * Currently we only have three use case to pass OriginType: KU, PRR, CU
 * OriginType reference: https://tiny.amazon.com/149ny2s9i
 * ActionProgram(programCode, program, channel) which returned from offers
        KINDLE_UNLIMITED("KINDLE_UNLIMITED", "KU","ALL_YOU_CAN_READ"),
        PRIME_READING("PRIME", "PRR", "PRIME_READING"),
        COMICS_UNLIMITED("COMICS_UNLIMITED", "CU", "ALL_YOU_CAN_READ"),
 */
const programCodeToOriginType:{ [key: string]: string } = {
    "KINDLE_UNLIMITED": "KindleUnlimited",
    "PRIME": "Prime",
    "COMICS_UNLIMITED": "ComicsUnlimited",
}

export const NativeAPI = {
    audioSamplePaused: (args: AudioSamplePausedArgs) => {
        getiOSAPI()?.audioSamplePaused?.postMessage(args);
    },
    popToRoot: () => {
        getiOSAPI()?.popToRoot?.postMessage({});
    },
    openWebPage: (url: string, viewTitle?: string): void => {
        const absoluteUrl = getAbsoluteUrl(url);
        absoluteUrl.searchParams.append('bypass_qv', 'true');
        url = absoluteUrl.href;
        if (getiOSAPI()) {
            getiOSAPI()?.openWebPage.postMessage({ url, viewTitle, reftag: "QuickView" });
        } else {
            window.open(url, "_blank");
        }
    },
    openBook: (params: OpenBookParams): void | Promise<void> => {
        const {
            asin,
            withAudioWhenBorrowedFrom,
            title,
            authors,
            type = CONTENT_TYPE.EBOK,
            coverImagePosition,
            programCode = ""
        } = params;
        if (getiOSAPI()) {
            const withAudio = withAudioWhenBorrowedFrom !== undefined;
            return getiOSAPI()?.openBook?.postMessage({
                asin,
                withAudio,
                type,
                coverImagePosition,
                originType: programCodeToOriginType[programCode],
            });
        }
    },
    getConnectionStatus: (): Promise<ConnectionState | void | null> | undefined => {
        if (getiOSAPI()) {
            return getiOSAPI()?.getConnectionStatus?.postMessage({}).then(
                it => { return { connectionState: getConnectionStateAsString(it?.connectionState) }; }
            );
        }
    },
    downloadBook: (asin: string, type: CONTENT_TYPE = CONTENT_TYPE.EBOK, title?: string): Promise<string | null> | undefined => {
        if (getiOSAPI()) {
            return getiOSAPI()?.downloadBook?.postMessage({ asin, type, title, });
        }
    },
    fetchMoreData: (pagingToken?: string): Promise<PagingResult> | undefined => {
        if (getiOSAPI()?.fetchMoreData) {
            return getiOSAPI()?.fetchMoreData?.postMessage({
                pagingToken: pagingToken
            });
        }
    },
    openWebPageExternal: (url: string) => {
        if (getiOSAPI()?.openWebPageExternal) {
            getiOSAPI()?.openWebPageExternal?.postMessage({ url });
            return true;
        }
        return false;
    },
    checkExternalLinkAccountEligibility: () => {
        return getiOSAPI()?.checkExternalLinkAccountEligibility?.postMessage({
            pageMetadata: {
                pageType: "detail",
                subPageType: "KindleQuickView-iOS",
            },
        });
    },
    openExternalAccountLink: (metadata: QuickViewAsinMetadata) => {
        if (getiOSAPI()?.openExternalAccountLink) {
            getiOSAPI()?.openExternalAccountLink?.postMessage({
                asin: metadata.asin,
                asinMetadata: {
                    physicalId: metadata.physicalId,
                    title: metadata.title,
                    primaryAuthor: metadata.authors,
                },
                pageMetadata: {
                    pageType: "detail",
                    subPageType: "KindleQuickView-iOS",
                },
                programBadgeMetadata: {
                    physicalId: metadata?.badgeImages?.lightThemeImage?.physicalId ?? "",
                    fileExtension: metadata?.badgeImages?.lightThemeImage?.extension ?? "svg",
                    physicalIdDark: metadata?.badgeImages?.darkThemeImage?.physicalId ?? "",
                    fileExtensionDark: metadata?.badgeImages?.darkThemeImage?.extension ?? "svg",
                    program: metadata.badgeImages?.altText ?? "",
                }
            });
            return true;
        }
        return false;
    },
    openReKindleInterstitial: (metadata: QuickViewAsinMetadata) => {
        if (getiOSAPI()?.openLinkOutInterstitial) {
            getiOSAPI()?.openLinkOutInterstitial?.postMessage({
                asin: metadata.asin,
                asinMetadata: {
                    physicalId: metadata.physicalId,
                    title: metadata.title,
                    primaryAuthor: metadata.authors,
                },
                pageMetadata: {
                    pageType: "detail",
                    subPageType: "KindleQuickView-iOS",
                },
                programBadgeMetadata: {
                    physicalId: metadata?.badgeImages?.lightThemeImage?.physicalId ?? "",
                    fileExtension: metadata?.badgeImages?.lightThemeImage?.extension ?? "svg",
                    physicalIdDark: metadata?.badgeImages?.darkThemeImage?.physicalId ?? "",
                    fileExtensionDark: metadata?.badgeImages?.darkThemeImage?.extension ?? "svg",
                    program: metadata.badgeImages?.altText ?? "",
                }
            });
            return true;
        }
        return false;
    },
    openTeenAccountError: (metadata: QuickViewAsinMetadata) => {
        if (getiOSAPI()?.openTeenAccountError) {
            return getiOSAPI()?.openTeenAccountError?.postMessage({
                type: 'asin',
                asin: metadata.asin,
                title: metadata.title,
                author: metadata.authors,
                clientId: 'kindle-qv',
            });
        }
    },
    launchQuickView: (asins: string[], initialIndex: number, args?: {title?: string, reftag?: string}): Promise<void> | undefined => {
        if (getiOSAPI()?.launchQuickView) {
            return getiOSAPI()?.launchQuickView?.postMessage({
                asins: asins,
                initialIndex: initialIndex,
            });
        }
    },
    getBatchBookOwnership: (asins: string[]) : Promise<BatchBookOwnershipResponse | null> | undefined => {
        if (getiOSAPI()) {
            return getiOSAPI()?.getBatchBookOwnership?.postMessage({ asins });
        }
        return undefined;
    },
    getBookStatus: (asin: string, type: CONTENT_TYPE = CONTENT_TYPE.EBOK): Promise<BookStatus | null> | undefined => {
        if (debug.get("disableGetBookStatus")) {
            return Promise.resolve(DEFAULT_BOOK_STATUS);
        }
        if (getiOSAPI()) {
            return getiOSAPI()?.getBookStatus?.postMessage({ asin, type });
        }
    },

    reportFastMetric: (schemaName: string, schemaVersion: number, payload: any): void => {
        if (getiOSAPI()) {
            getiOSAPI()?.reportFastMetric.postMessage({ schemaName: schemaName, schemaVersion: schemaVersion, payload: payload });
        }
    },
    reportBatchFastMetrics: (metrics: BatchedMetric[]): void => {
        if (getiOSAPI()) {
            getiOSAPI()?.reportBatchFastMetrics?.postMessage({ metrics });
        }
    },
    didFail: (message?: string): boolean => {
        if (getiOSAPI()?.didFail) {
            getiOSAPI()?.didFail?.postMessage({ message });
            return true;
        }
        return false;
    },
    reportActionMetric: (action: string, actionType: string): void => {
        if (getiOSAPI()) {
            getiOSAPI()?.reportActionMetric.postMessage({ action: action, actionType: actionType });
        }
    },
    dismissQuickView: (finalIndex?: number): void => {
        if (getiOSAPI()?.dismissQuickView) {
            getiOSAPI()?.dismissQuickView.postMessage({ finalIndex });
        }
    },
    syncLibrary: (): void => {
        if (getiOSAPI()) {
            getiOSAPI()?.syncLibrary?.postMessage({});
        }
    },
    updateQuickViewPosition: (index: number): void => {
        if (getiOSAPI()?.updateQuickViewPosition) {
            getiOSAPI()?.updateQuickViewPosition.postMessage({ index });
        }
    },
    httpRequest: (args: HttpRequestArgs): Promise<string | null> => {
        const path = args.path;
        const method = args.method ?? "GET";
        const headers = args.headers;
        const queryParams = args.queryParams;
        const body = args.body;
        const preferFetch = args.preferNative !== true;

        const iOSAPI = getiOSAPI();
        if (iOSAPI?.httpRequest) {
            return iOSAPI.httpRequest.postMessage({
                path,
                method,
                queryParams,
                headers,
                body,
            });
        }

        if (preferFetch && (path.startsWith("https:") || (location.protocol === "https:" && !location.hostname.endsWith("dev")))) {
            const urlPath = path.startsWith("https:") ? path : `${location.origin}${path}`;
            return fetch(setQueryParams(urlPath, queryParams), {
                credentials: "include",
                mode: "cors",
                method,
                headers,
                body,
            }).then((response) => response.text());
        }

        // Fall back to regular fetch request when native APIs are unavailable.
        // During local development use a relative path for webpack-dev-server proxying
        // to avoid CORS issues when making requests to amazon.com from localhost
        const domain = (location.protocol === "http:")
            ? ""
            : getCurrentBaseUrl();

        return fetch(setQueryParams(`${domain}${path}`, queryParams), {
            credentials: "include",
            mode: "cors",
            method,
            headers,
            body,
        }).then((response) => response.text());
    },
    share: (shareData: ShareData): Promise<void> => {
        if (typeof navigator.share === "function") {
            return navigator.share(shareData);
        }
        return Promise.reject();
    },
};

export const DEFAULT_BOOK_STATUS: BookStatus = {
    bookState: BookState.AZPluginBookStateInLibraryUnknown,
    downloadProgress: 0,
};

/**
 * Read the TOS webview parameters available when loading within the Kindle app.
 * These values can be used to get the theme, locale, etc.
 */
export function getStoreWebviewParameters(): KindleStoreParametersI | undefined {
    const storeCookieValue = getCookieValue(KINDLE_STORE_COOKIE_NAME);

    if (!storeCookieValue) {
        return undefined;
    }

    return storeCookieValue
        .replace(/"/g, "")
        .split("&")
        .reduce((parameters: KindleStoreParametersI, parameter: string) => {
            const parts = parameter.split("=");

            if (parts.length === 2) {
                const key = parts[0] as keyof KindleStoreParametersI;
                parameters[key] = parts[1];
            }

            return parameters;
        }, {});
}

export const getBDSDeviceType = (deviceContext: DeviceDetailsI): DeviceType => {
    if (deviceContext.isDesktop) return "desktop";
    else if (deviceContext.isTablet) return "tablet";
    else return "mobile"; 
  };


export function getBDSOperatingSystem(deviceDetails: DeviceDetailsI): OperatingSystem {
    if (deviceDetails.isIOS) return "iOS";
    else return "default";
}

export function getBDSLocale(locale: string): Locale {
    return locale as Locale
}

const DOMAIN_TO_MARKETPLACE_MAP: Record<string, MarketplaceId> = {
    "amazon.com": "ATVPDKIKX0DER",      //US
    "amazon.ca": "A2EUQ1WTGCTBG2",      //CA
    "amazon.com.mx": "A1AM78C64UM0Y8",  //MX
    "amazon.co.uk": "A1F83G8C2ARO7P",   //UK
    "amazon.it": "APJ6JRA9NG5V4",       //IT
    "amazon.de": "A1PA6795UKMFR9",      //DE
    "amazon.es": "A1RKKUPIHCS9HS",      //ES
    "amazon.in": "A21TJRUUN4KGV",       //IN
    "amazon.fr": "A13V1IB3VIYZZH",      //FR
    "amazon.nl": "A1805IZSGTT6HS",      //NL
    "amazon.ae": "A2VIGQ35RCS4UG",      //AE
    "amazon.sa": "A17E79C6D8DWNP",      //SA
    "amazon.com.tr": "A33AVAJ2PDY3EV",  //TR
    "amazon.co.jp": "A1VC38T7YXB528",   //JP
    "amazon.com.au": "A39IBJ37TRP1C6",  //AU
    "amazon.sg": "A19VAU5U5O7RUS",      //SG
    "amazon.cn": "AAHKV2X7AFYLW",       //CN
}

export function getBDSMarketplace(domain: string): MarketplaceId {
    return DOMAIN_TO_MARKETPLACE_MAP[domain] ?? "default";
}
